import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { startGemsPinchSequence } from '../shop/Helpers';
import { Feature } from 'src/replicant/analytics';
import * as Inventory from 'src/replicant/inventory';
import { PetEvolutionStage } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import * as Care from 'src/replicant/care';
import { GrowthCapsulePanel } from './GrowthCapsulePanel';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupConfirmGrowthCapsule extends PopupBasic {
  private close: CloseHandler;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      titleIcon: {
        image: 'assets/ui/growthacceleration/growthcapsule_icon.png',
      },
      tracking: {
        feature: Feature.GROWTHCAPSULE._,
        $subFeature: Feature.GROWTHCAPSULE.PURCHASE_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        width: this.message.style.width,
      }),
    );

    const costRow = this.box.addSubview(new ImageSliceView(ui.costRow));

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('purchaseWithGems.requiredGems'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.box.addSubview(new ImageSliceView(ui.balanceRow));

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('purchaseWithGems.balanceAfterPurchase'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        image: 'assets/ui/hud/icons/hud_gem.png',
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.GROWTHCAPSULE._,
          $subFeature: Feature.GROWTHCAPSULE.PURCHASE_BACK,
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.box,
      localeText: () => i18n('basic.okay'),
      onClick: async () => {
        sounds.sfx.play('ok');
        return opts.close(true);
      },
      tracking: {
        feature: Feature.GROWTHCAPSULE._,
        $subFeature: Feature.GROWTHCAPSULE.PURCHASE_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/growthacceleration/growthcapsule_icon.png',
      'assets/ui/hud/icons/hud_gem.png',
    ]);
  }

  public init() {
    super.init({
      title: i18n('growthcapsule.purchase.title'),
      message: i18n('growthcapsule.purchase.message'),
    });

    const evolution = Pet.getters.getCurrentEvolution(
      StateObserver.getState().user,
    );

    if (!evolution) return;

    const price = Care.ruleset.getGrowthCapsulePrice(evolution.stage);

    this.box.addSubview(new GrowthCapsulePanel());
    this.cost.localeText = () => parseAmount(price);

    const balance = this.getBalanceAfterPurchase(price);
    this.balance.localeText = () => parseAmount(balance);

    this.okButton.onClick = async () => {
      sounds.sfx.play('ok');

      const canPurchase = this.canPurchase(price);
      this.close(canPurchase);

      if (!canPurchase) {
        this.trackPinchEvent(evolution.stage);
        await startGemsPinchSequence({
          feature: 'growthcapsule',
          crossplayTracking: {
            feature: 'growthcapsule',
            subFeature: 'growthcapsule',
          },
        });
      }
    };

    if (!this.canPurchase(price)) {
      this.balance.updateOpts({ color: 'red' });
    }
  }

  /**
   * Check if player can purchase care item
   */
  private canPurchase(price: number) {
    return this.getBalanceAfterPurchase(price) >= 0;
  }

  /**
   * Get balance after purchase, can be negative
   */
  private getBalanceAfterPurchase(price: number) {
    return Inventory.getters.getGems(StateObserver.getState().user) - price;
  }

  private trackPinchEvent(stage: PetEvolutionStage) {
    trackPinchEvent({
      resource: 'gems',
      trigger: `growthcapsule`,
      item_id: stage,
    });
  }
}
